<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { tick } from 'svelte';
    import AudioRecordingInteractionImpl from './AudioRecordingInteractionImpl.svelte';
    import AudioRecordingUploader from './AudioRecordingUploader.svelte';
    import { getItemSettingsStore } from '../../../itemsSettingsStore.js';
    import { getItemSequentialInteractionsStore } from '../../../itemsSequentialInteractionsStore.js';
    import { hasClass } from '../../util/attributes.js';
    import { semverCompare } from 'taoDeliverAppsCommon/util/semver.js';

    let {
        itemIdentifier,
        properties,
        classes = '',
        responseIdentifier,
        version,
        // may be turned on by itemRunnerConfig propertyOverride:
        useUploader = false,
        ...restProps
    } = $props();

    // svelte-ignore state_referenced_locally
    const sequentialInteractionsStore = getItemSequentialInteractionsStore(itemIdentifier);
    // svelte-ignore state_referenced_locally
    const itemSettingsStore = getItemSettingsStore(itemIdentifier);

    // svelte-ignore non_reactive_update
    let isReviewMode, supportsUploader, isSequential;

    setDerivedProps();

    function setDerivedProps() {
        isReviewMode = !!properties?.isReviewMode;
        supportsUploader = semverCompare(version, '1.3.0') >= 0;
        isSequential = hasClass(classes, 'sequential') && !isReviewMode;

        if (isSequential) {
            sequentialInteractionsStore.register(responseIdentifier);
        }
    }

    //Remount the interaction outside of usual item lifecycle.
    //On initial Item mount, always render to allow normal ui flow with registerLoadingElement in CustomInteractionDefault,
    // but do not autostart the recording
    let isInitialMount = $state(true);
    let doNotPlayMedia = $state(!!$itemSettingsStore.doNotPlayMedia); //skip one change

    $effect(() => {
        let newDoNotPlayMedia = !!$itemSettingsStore.doNotPlayMedia;
        if (doNotPlayMedia !== newDoNotPlayMedia) {
            tick().then(() => {
                //tick: because Item will trigger 'stateupdate', let's ensure that PCI is destroyed only after that
                doNotPlayMedia = newDoNotPlayMedia;
                isInitialMount = false;
            });
        }
    });
</script>

<style>
    .audio-recording-wrapper {
        height: 100px;
    }
</style>

<div class="audio-recording-wrapper">
    {#if (useUploader || isReviewMode) && supportsUploader}
        <!-- uploader part should stay mounted, even if doNotPlayMedia becomes set -->
        <AudioRecordingUploader
            {...restProps}
            {itemIdentifier}
            {responseIdentifier}
            {properties}
            {classes}
            {isInitialMount}
            {doNotPlayMedia} />
    {:else}
        {#key doNotPlayMedia && !isInitialMount}
            {#if !doNotPlayMedia || isInitialMount}
                <AudioRecordingInteractionImpl
                    {...restProps}
                    {itemIdentifier}
                    {responseIdentifier}
                    {properties}
                    {classes}
                    {isInitialMount}
                    {doNotPlayMedia} />
            {/if}
        {/key}
    {/if}
</div>
